/**
 * Old Mountain Tea — "solid header once past the hero photo" behaviour.
 *
 * Horizon flips the transparent header to its solid (white) state as soon as
 * `data-sticky-state` becomes `active`, which the IntersectionObserver in
 * header.js sets after a single pixel of scroll. The brief asks for the swap to
 * happen when the visitor scrolls past the background mountain image, so this
 * script measures the hero element and toggles `data-omt-solid` on
 * `#header-component`; the matching rules in sections/header.liquid key off that
 * attribute instead.
 *
 * The hero is `.omt-band` (sections/omt-page-hero.liquid — interior pages,
 * collection, blog, and the PDP's compact band) or `.hero` (the homepage
 * sections/hero.liquid). Both render as the page's first section by theme
 * convention, so the first document-order match is the right one.
 *
 * getBoundingClientRect() is viewport-relative and the header is fixed to the
 * viewport top, so the decision needs no scroll-container arithmetic. The
 * scroll-container split from scroll-container.js (at >=990px Horizon scrolls
 * `.page-wrapper`, below that the document root scrolls natively) is kept only
 * for attaching the listener and for the no-hero fallback. Kept as a plain
 * script (no imports) so it does not depend on the theme importmap.
 *
 * Compact sticky header (desktop): while the sticky header is solid, the
 * script also sets `data-omt-compact`, which sections/header.liquid uses to
 * shrink the logo and row padding. The full (uncompacted) height is kept in
 * `--omt-header-full-height` on <body>. The layout rules there use it instead of
 * the live `--header-height`, so the page below does not jump when the bar
 * shrinks. The hero test uses that full height too; the shrinking header
 * would otherwise flip itself back out of the solid state.
 */
(function () {
  var SQUEEZE = window.matchMedia('(min-width: 990px)');
  var rafId = null;
  var boundTarget = null;
  var fullHeight = 0;

  function scrollContainer() {
    if (SQUEEZE.matches) {
      return document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  function scrollEventTarget() {
    if (SQUEEZE.matches) {
      return document.querySelector('.page-wrapper') || document;
    }
    return document;
  }

  // The mountain band: omt-page-hero renders `.omt-band`, the homepage hero
  // section renders `.hero`. Queried fresh each frame so section reloads in the
  // theme editor pick up a replaced hero without a rebind.
  function heroElement() {
    return document.querySelector('.omt-band, .hero');
  }

  function update() {
    rafId = null;

    var header = document.getElementById('header-component');
    if (!header) return;

    var hero = heroElement();
    var solid;

    // Measure only while uncompacted; once compact, the cached value stands.
    if (!header.hasAttribute('data-omt-compact') || !fullHeight) {
      fullHeight = header.getBoundingClientRect().height;
    }

    if (header.getAttribute('transparent') === 'always') {
      // Non-sticky transparent headers (the journal index and articles) scroll
      // away with the page and never go solid; flagging them would only swap
      // the white nav text for ink over the dark ground.
      solid = false;
    } else if (hero) {
      // Solid once the bottom edge of the photo has slid under the header.
      solid = hero.getBoundingClientRect().bottom <= fullHeight;
    } else {
      // Templates with no hero (cart, search, 404, policies) are not
      // transparent-header pages; fall back to Horizon's stock behaviour.
      solid = scrollContainer().scrollTop > 0;
    }

    if (solid) {
      header.setAttribute('data-omt-solid', 'true');
    } else {
      header.removeAttribute('data-omt-solid');
    }

    if (solid && SQUEEZE.matches && header.hasAttribute('sticky')) {
      document.body.style.setProperty('--omt-header-full-height', Math.round(fullHeight) + 'px');
      header.setAttribute('data-omt-compact', 'true');
    } else {
      header.removeAttribute('data-omt-compact');
    }
  }

  function onScroll() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(update);
  }

  function bind() {
    if (boundTarget) boundTarget.removeEventListener('scroll', onScroll);
    boundTarget = scrollEventTarget();
    boundTarget.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  // Re-bind when the scroll container swaps at the squeeze breakpoint, and
  // re-measure whenever the page height or the header markup changes.
  SQUEEZE.addEventListener('change', bind);
  window.addEventListener('resize', onScroll, { passive: true });
  window.addEventListener('pageshow', bind);
  document.addEventListener('shopify:section:load', bind);
})();
